import { useState } from 'react';
import {
  Button, Card, Col, Divider, Form, Input, InputNumber, Modal,
  Row, Select, Space, Switch, Table, Tag, Typography,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useMenu } from '../context/MenuContext';
import type { MenuItem } from '../api/menu';

const { Title, Text } = Typography;

export default function MenuEditor() {
  const { menu, updateInfo, addCategory, deleteCategory, addItem, updateItem, deleteItem, toggleItemAvailability } = useMenu();
  const [itemModal, setItemModal] = useState<{ mode: 'add' | 'edit'; item?: MenuItem } | null>(null);
  const [catModal, setCatModal] = useState(false);
  const [form] = Form.useForm();
  const [catForm] = Form.useForm();
  const [infoForm] = Form.useForm<{ restaurantName: string; description: string; currency: string }>();

  function openAddItem() { form.resetFields(); setItemModal({ mode: 'add' }); }
  function openEditItem(item: MenuItem) { form.setFieldsValue(item); setItemModal({ mode: 'edit', item }); }

  function saveItem(values: Omit<MenuItem, 'id'>) {
    if (itemModal?.mode === 'edit' && itemModal.item) {
      updateItem(itemModal.item.id, values);
    } else {
      addItem({ ...values, available: true, tags: values.tags ?? [] });
    }
    setItemModal(null);
  }

  function saveCategory(values: { name: string; description?: string }) {
    addCategory(values.name, values.description);
    catForm.resetFields();
    setCatModal(false);
  }

  const columns = [
    { title: 'Item', dataIndex: 'name', key: 'name', render: (n: string, row: MenuItem) => (
      <div>
        <strong>{n}</strong>
        {row.description && <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{row.description}</div>}
      </div>
    )},
    { title: 'Category', dataIndex: 'category', key: 'category',
      render: (id: string) => {
        const cat = menu.categories.find(c => c.id === id);
        return cat ? <Tag>{cat.name}</Tag> : <Tag color="red">Unknown</Tag>;
      }},
    { title: 'Price', dataIndex: 'price', key: 'price', align: 'right' as const,
      render: (v: number) => `${menu.currency} ${v.toFixed(2)}` },
    { title: 'Tags', dataIndex: 'tags', key: 'tags',
      render: (tags: string[]) => tags?.map(t => <Tag key={t} color="blue">{t}</Tag>) },
    { title: 'Available', dataIndex: 'available', key: 'available',
      render: (v: boolean, row: MenuItem) => (
        <Switch checked={v} size="small" onChange={() => toggleItemAvailability(row.id)} />
      )},
    { title: '', key: 'actions',
      render: (_: unknown, row: MenuItem) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEditItem(row)} />
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => deleteItem(row.id)} />
        </Space>
      )},
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 24 }}>Menu Editor</Title>

      <Card title="Restaurant Info" style={{ marginBottom: 16 }}>
        <Form
          form={infoForm}
          layout="inline"
          initialValues={{ restaurantName: menu.restaurantName, description: menu.description, currency: menu.currency }}
          onFinish={updateInfo}
        >
          <Form.Item name="restaurantName" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input style={{ width: 280 }} />
          </Form.Item>
          <Form.Item name="currency" label="Currency">
            <Select style={{ width: 90 }}>
              {['USD','THB','EUR','GBP','SGD','AUD'].map(c => <Select.Option key={c}>{c}</Select.Option>)}
            </Select>
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">Save</Button>
          </Form.Item>
        </Form>
      </Card>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={12}>
          <Card
            title="Categories"
            extra={<Button size="small" icon={<PlusOutlined />} onClick={() => setCatModal(true)}>Add</Button>}
          >
            {menu.categories.length === 0
              ? <Text type="secondary">No categories yet.</Text>
              : (
                <Space direction="vertical" style={{ width: '100%' }}>
                  {menu.categories.map(cat => (
                    <div key={cat.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
                      <div>
                        <strong>{cat.name}</strong>
                        {cat.description && <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>{cat.description}</Text>}
                        <Tag style={{ marginLeft: 8 }}>{menu.items.filter(i => i.category === cat.id).length} items</Tag>
                      </div>
                      <Button size="small" danger icon={<DeleteOutlined />} onClick={() => deleteCategory(cat.id)} />
                    </div>
                  ))}
                </Space>
              )
            }
          </Card>
        </Col>
        <Col span={12}>
          <Card title="Quick Stats">
            <Row gutter={16}>
              <Col span={8}><div style={{ textAlign: 'center' }}><div style={{ fontSize: 24, fontWeight: 700 }}>{menu.items.length}</div><Text type="secondary">Items</Text></div></Col>
              <Col span={8}><div style={{ textAlign: 'center' }}><div style={{ fontSize: 24, fontWeight: 700 }}>{menu.categories.length}</div><Text type="secondary">Categories</Text></div></Col>
              <Col span={8}><div style={{ textAlign: 'center' }}><div style={{ fontSize: 24, fontWeight: 700 }}>{menu.items.filter(i => i.available).length}</div><Text type="secondary">Available</Text></div></Col>
            </Row>
          </Card>
        </Col>
      </Row>

      <Card
        title="Menu Items"
        extra={<Button type="primary" icon={<PlusOutlined />} onClick={openAddItem}>Add Item</Button>}
      >
        <Table dataSource={menu.items} columns={columns} rowKey="id" size="small" pagination={{ pageSize: 15 }} />
      </Card>

      {/* Add Item Modal */}
      <Modal
        title={itemModal?.mode === 'edit' ? 'Edit Item' : 'Add Menu Item'}
        open={!!itemModal}
        onCancel={() => setItemModal(null)}
        onOk={() => form.submit()}
        okText="Save"
      >
        <Form form={form} layout="vertical" onFinish={saveItem}>
          <Row gutter={12}>
            <Col span={14}><Form.Item name="name" label="Name" rules={[{ required: true }]}><Input /></Form.Item></Col>
            <Col span={10}><Form.Item name="price" label="Price" rules={[{ required: true }]}><InputNumber min={0} step={0.5} style={{ width: '100%' }} /></Form.Item></Col>
          </Row>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="category" label="Category" rules={[{ required: true }]}>
            <Select>
              {menu.categories.map(c => <Select.Option key={c.id} value={c.id}>{c.name}</Select.Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="tags" label="Tags (e.g. vegan, spicy, gluten-free)">
            <Select mode="tags" placeholder="Add tags" />
          </Form.Item>
          <Form.Item name="image" label="Image URL (optional)">
            <Input placeholder="https://..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* Add Category Modal */}
      <Modal
        title="Add Category"
        open={catModal}
        onCancel={() => setCatModal(false)}
        onOk={() => catForm.submit()}
        okText="Add"
      >
        <Form form={catForm} layout="vertical" onFinish={saveCategory}>
          <Form.Item name="name" label="Category Name" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="description" label="Description (optional)"><Input /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
